// The material's margin marks in the panel (right panel v2): per element, its
// grounding (no knowledge-bank claim behind a statement → unsupported, the
// computed read of knowledge-bank.md §11.6; else backed → grounded with each
// claim's source passage as an anchor) and an advisory mark on each heavy section. The
// same quality read the two lenses render, so the margin and the lenses can
// never disagree.
//
// Pure: a fold over the quality read. `locatorOf` names a citation's place in
// its source ("p. 44"), supplied by the view so the copy stays localized.

import type { ElementAnnotation } from "../../../components/Editor/MaterialDocument/MaterialDocument.js";
import type { GutterMarker } from "../../../components/Editor/types.js";
import type {
    GroundingState,
    SourceAnchor,
} from "../../../components/Grounding/types.js";
import {
    type MaterialQuality,
    type StatementSource,
    statementSourcesOf,
} from "../../../components/QualitySummary/QualitySummary.js";
import { pedagogicalQualityModel } from "../reviewQualityModel.js";

const groundingOf = (
    rows: readonly StatementSource[],
    locatorOf: (row: StatementSource) => string,
): GroundingState => {
    const anchor = (row: StatementSource): SourceAnchor => ({
        type: "resolved",
        quote: row.quote,
        sourceTitle: row.source.title,
        locator: locatorOf(row),
    });
    const [first, ...rest] = rows;
    return first === undefined
        ? { type: "ungrounded" }
        : { type: "grounded", anchors: [anchor(first), ...rest.map(anchor)] };
};

export const annotationsOf = (
    quality: MaterialQuality | null,
    locatorOf: (row: StatementSource) => string,
): ReadonlyMap<string, ElementAnnotation> => {
    const rows = statementSourcesOf(quality);
    const unsupported: readonly string[] =
        quality === null || quality.grounding.type === "no-sources"
            ? []
            : quality.grounding.support.flatMap((k) =>
                  k.uncited.map((item) => item.id),
              );
    const readability = pedagogicalQualityModel(quality).readability;
    const heavy =
        readability.type === "checked"
            ? readability.heaviestSections.map((s) => s.id)
            : [];
    const ids = [
        ...rows.map((r) => r.statementId),
        ...unsupported,
        ...heavy,
    ].filter((id, i, all) => all.indexOf(id) === i);
    return new Map(
        ids.map((id): [string, ElementAnnotation] => {
            const own = rows.filter((r) => r.statementId === id);
            const grounding: readonly GutterMarker[] = unsupported.includes(id)
                ? [{ type: "grounding", state: { type: "unsupported" } }]
                : own.length > 0
                  ? [
                        {
                            type: "grounding",
                            state: groundingOf(own, locatorOf),
                        },
                    ]
                  : [];
            const issue: readonly GutterMarker[] = heavy.includes(id)
                ? [{ type: "issue", severity: "warning" }]
                : [];
            return [id, { markers: [...grounding, ...issue] }];
        }),
    );
};
